import axios from 'axios';
import Cookies from 'js-cookie';

import { dispatchAction, navigateTo } from '../utils/navigation';

// Диспатчим ОБЫЧНЫЕ объекты действий, а не импортированные из слайсов
// action-creator'ы. Redux не требует, чтобы объект действия был создан
// именно импортированной функцией - важен только type. Если бы мы
// импортировали setErrorUser из userSlice.js (а тот импортирует
// api/userinfo.js, а тот - этот же api.js) или resetStore, получилась
// бы циклическая зависимость (import-x/no-cycle) - именно так и было
// на первой попытке этого фикса.
const USER_SET_ERROR = 'user/setErrorUser'; // см. userSlice.js
const RESET_STORE = 'resetStore'; // см. store/resetStore.js

const API_URL = process.env.REACT_APP_API_URL;
const api = axios.create({
  baseURL: API_URL,
  // withCredentials обязателен для httpOnly-куки: фронтенд и API на
  // разных доменах (ambar-sklad.ru / kosmoslogistic.ru), без этого флага
  // браузер не пришлёт куку в межсайтовом запросе. Это основной,
  // рабочий путь авторизации для обычного входа по SMS.
  withCredentials: true,
  headers: {
    // 'Content-Type': 'application/json',
  },
});

// Отдельный, параллельный способ авторизации - для входа по служебной
// ссылке /init?token=... (см. InitPage.jsx). Такая ссылка используется
// только сотрудниками внутри защищённого контура (не публичная функция
// для клиентов), и токен под неё выпускается с ограниченным
// функционалом, отдельно от обычной пользовательской сессии.
//
// Кука называется init_auth_token, НЕ auth_token - раньше называлась
// так же, но после перехода на same-origin proxy обычная httpOnly-
// сессия тоже стала жить на этом домене, и одинаковое имя создавало
// коллизию: если на машине уже была валидная httpOnly-сессия, браузер
// (по стандартному защитному поведению) отклонял попытку JS
// перезаписать куку тем же именем - вход по служебной ссылке молча не
// срабатывал именно на таких машинах. Разные имена убирают коллизию
// структурно.
//
// Механизм: InitPage.jsx кладёт токен в обычную, читаемую куку НА
// ДОМЕНЕ ФРОНТЕНДА (не httpOnly - JS сам её туда положил, значит сам
// может и прочитать). Здесь она читается и подставляется в заголовок
// Authorization - ровно так, как это делалось до перехода на httpOnly
// для обычного входа по SMS. Обычный вход продолжает идти через
// withCredentials + httpOnly-куку с домена бэкенда, эти два способа
// не конфликтуют.
api.interceptors.request.use(
  (config) => {
    const token = Cookies.get('init_auth_token');
    if (token) {
      config.headers.Authorization = token;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => {
    sessionStorage.setItem('redirect', 'false');
    sessionStorage.setItem('isError', 'false');
    console.log('SUCCESS:', response);

    return response;
  },

  async (error) => {
    // Сервер вернул ответ с ошибкой
    const data = error?.response?.data;
    console.log(data, error);
    if (error.code === 'ERR_NETWORK') {
      // Различаем: браузер реально офлайн — или просто этот конкретный
      // запрос не прошёл (CORS, блокировщик рекламы, разовый сбой),
      // а с интернетом всё в порядке. Во втором случае не трогаем всё
      // приложение — просто пробрасываем ошибку вызывающему коду.
      if (!navigator.onLine) {
        const redirect = JSON.parse(sessionStorage.getItem('redirect'));
        sessionStorage.setItem('isError', 'true');
        if (!redirect) {
          // Раньше здесь был window.location.href = '/' - жёсткая
          // перезагрузка всего приложения. SPA-переход дешевле, но сам
          // по себе не гарантирует, что состояние "нет интернета"
          // где-то отобразится: Homepage.jsx раньше читал этот же флаг
          // из sessionStorage в своём эффекте при монтировании - если
          // пользователь уже на "/", навигация на тот же маршрут не
          // перемонтирует компонент, и эффект не сработает повторно.
          // Поэтому диспатчим состояние ошибки прямо здесь, без
          // посредников - работает независимо от того, какая страница
          // сейчас открыта и перемонтируется ли она.
          //
          // Кстати, раньше это всё равно не работало: Homepage сравнивал
          // error с конкретной строкой 'Отсутствует подключение к
          // интернету', а sessionStorage хранил просто 'true' -
          // JSON.parse('true') даёт булево true, а не эту строку,
          // сравнение никогда не совпадало. Теперь кладём ту же самую
          // строку, что использует существующий рабочий путь (ошибка
          // сети при загрузке userinfo, см. userSlice.js).
          dispatchAction({
            type: USER_SET_ERROR,
            payload: 'Отсутствует подключение к интернету',
          });
          navigateTo('/');
        }
        sessionStorage.setItem('redirect', 'true');
      }

      return Promise.reject(error);
    }
    if (data?.error === 'access_denied') {
      console.warn('⛔ 401 Unauthorized — выполняется выход');

      // Раньше здесь была Cookies.remove('auth_token') - JS больше не
      // видит и не может удалить httpOnly-куку. Если сервер счёл её
      // невалидной (иначе не ответил бы access_denied), дальше она нам
      // не поможет в любом случае - resetStore + переход на /login
      // достаточны с точки зрения фронтенда.
      //
      // Раньше здесь был window.location.href = '/' - жёсткая
      // перезагрузка, которая попутно обнуляла весь Redux-стор просто
      // потому что приложение поднималось с нуля. При SPA-переходе это
      // нужно сделать явно - resetStore() - тот же паттерн, что уже
      // использует настоящая кнопка "Выйти" в UserMenuDropdown.jsx и
      // AccountPage.jsx. Переходим сразу на /login (а не на '/'), как и
      // там - ProtectedRoute всё равно перекинул бы с '/' туда же, но
      // без лишнего промежуточного рендера.
      dispatchAction({ type: RESET_STORE });
      navigateTo('/login');

      // Можно вернуть пустой reject, чтобы остановить логику выше
      return Promise.reject(error);
    }
    // Остальные ошибки — просто пробрасываем дальше
    return Promise.reject(error);
  }
);

export default api;
